import debounce from 'lodash/debounce';
import { useState, useMemo, useCallback } from 'react';

type UseAutocompleteSearchProps = {
  debounceTime?: number;
};

type UseAutocompleteSearchReturn = {
  inputValue: string;
  searchValue: string;
  onSearch: (value: string) => void;
  setInputValue: (value: string) => void;
};

export const useAutocompleteSearch = ({
  debounceTime = 300,
}: UseAutocompleteSearchProps = {}): UseAutocompleteSearchReturn => {
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchValue(value);
      }, debounceTime),
    [debounceTime],
  );

  const onSearch = useCallback(
    (value: string) => {
      setInputValue(value);

      // When clearing, immediately reset searchValue and cancel any pending debounce
      if (value === '') {
        debouncedSearch.cancel();
        setSearchValue('');
        return;
      }

      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  return {
    inputValue,
    searchValue,
    onSearch,
    setInputValue,
  };
};
